import cloneDeep from "lodash.clonedeep";
import maxBy from "lodash.maxby";
import { v4 } from "uuid";

import {
  RedirectDto,
  RedirectFilter,
  ShopifyTemplateTypeType,
  UrlRedirectType,
  VariationDto,
} from "@intelligems/ig-types";
import { VariationFe } from "@intelligems/ig-types/fe-only/variations";
import * as rtk from "@reduxjs/toolkit";
import { createSelector, type PayloadAction } from "@reduxjs/toolkit";
import {
  getSpecificRedirectsUtil,
  getSpecificVariationRedirectsUtil,
} from "@src/features/experiments/components/redirects/getSpecificRedirects";

import { initialRedirectState } from "../../api/initials";
import {
  ensureAllTemplateType,
  ensureRedirectGroupOrder,
  ensureTemplateSuffix,
  ensureTestGroupRedirects,
} from "../../api/reducers/redirects/redirectReducerActions";
import { IRedirectsSliceState } from "../types/redirects";

const sliceInitialRedirectsState: IRedirectsSliceState = {
  isSuccess: false,
  redirects: [] as RedirectDto[],
  storedRedirects: [] as RedirectDto[],
  redirectErrors: [] as string[],
};

const redirectsSlice = rtk.createSlice({
  name: "redirects",
  initialState: sliceInitialRedirectsState,
  reducers: {
    resetRedirects: (draft) => {
      draft.isSuccess = false;
      draft.redirects = [];
      draft.storedRedirects = [];
      draft.redirectErrors = [];
    },
    setOfferTestRedirects: (
      draft,
      action: PayloadAction<{ redirects: RedirectDto[]; variationId: string }>
    ) => {
      const updatedDraft = draft.redirects.filter(
        (prod) => prod.variationId !== action.payload.variationId
      );

      const dupRedirects: RedirectDto[] = action.payload.redirects.map(
        (redirect) => {
          return {
            ...redirect,
            id: v4(),
            variationId: action.payload.variationId,
          };
        }
      );

      draft.redirects = [...updatedDraft, ...dupRedirects];
    },
    resetOfferTestRedirects: (
      draft,
      action: PayloadAction<{ variationId: string }>
    ) => {
      draft.redirects = draft.redirects.filter(
        (prod) => prod.variationId !== action.payload.variationId
      );
    },
    initRedirectsState: (
      draft,
      action: PayloadAction<{ redirects: RedirectDto[]; domain: string }>
    ) => {
      draft.isSuccess = true;
      draft.redirects = action.payload.redirects;
      draft.storedRedirects = action.payload.redirects;
      draft.redirectErrors = [];
    },
    initRedirectsFromVariationsState: (
      draft,
      action: PayloadAction<{ variations: VariationDto[] }>
    ) => {
      const { variations } = action.payload;
      const tempRedirects: RedirectDto[] = [];

      variations.forEach((variation) => {
        if (variation.redirects) {
          tempRedirects.push(...variation.redirects);
        }
      });

      if (tempRedirects.length) {
        draft.isSuccess = true;
        draft.redirects = tempRedirects;
        draft.storedRedirects = tempRedirects;
      }
    },
    initUrlRedirectsState: (
      draft,
      action: PayloadAction<{
        variations: VariationFe[];
        isPersonalization?: boolean;
      }>
    ) => {
      draft.isSuccess = true;
      draft.redirectErrors = [];

      action.payload.variations.forEach((variation: VariationFe) => {
        draft.redirects.push(
          initialRedirectState({
            variation,
            group: 1,
            isThemeTest: false,
            isTemplateTest: false,
            skip: action.payload.isPersonalization ? false : undefined,
          })
        );
      });
      draft.storedRedirects = draft.redirects;
    },
    checkRedirects: (
      draft,
      action: PayloadAction<{ variations: VariationFe[] }>
    ) => {
      draft.redirects = ensureTestGroupRedirects(
        draft.redirects,
        action.payload.variations
      );
    },
    addNewRedirectOrigin: (
      draft,
      action: PayloadAction<{
        variations: VariationFe[];
        filter: RedirectFilter;
        isPersonalization?: boolean;
      }>
    ) => {
      const group = (maxBy(draft.redirects, (_) => _.group)?.group || 0) + 1;
      action.payload.variations.forEach((variation: VariationFe) => {
        draft.redirects.push(
          initialRedirectState({
            variation,
            group,
            isThemeTest: false,
            redirectFilter: action.payload.filter,
            skip: action.payload.isPersonalization ? false : undefined,
          })
        );
      });
    },
    updateAllRedirectFilters: (
      draft,
      action: PayloadAction<{
        filter: RedirectFilter;
      }>
    ) => {
      draft.redirects.forEach((redirect) => {
        redirect.filter = action.payload.filter;
      });
    },
    updateAllUrlRedirectType: (
      draft,
      action: PayloadAction<{
        redirectType: UrlRedirectType;
      }>
    ) => {
      draft.redirects.forEach((redirect) => {
        redirect.redirectType = action.payload.redirectType;
      });
    },
    deleteAllUrlRedirects: (draft) => {
      draft.redirects = draft.redirects.filter(
        (item) => item.isThemeTest || item.isTemplateTest
      );
    },
    updateOriginUrl: (
      draft,
      action: PayloadAction<{
        group: number;
        value: string;
        queryParams?: { key: string; value: string }[];
        redirectId?: string;
      }>
    ) => {
      // offer test redirects
      if (action.payload.redirectId) {
        const index = draft.redirects.findIndex(
          (redirect) => redirect.id === action.payload.redirectId
        );
        if (index !== -1) {
          draft.redirects[index].originUrl = action.payload.value;
        }
      } else {
        for (const redirect of draft.redirects) {
          if (redirect.group === action.payload.group) {
            redirect.originUrl = action.payload.value;
            if (action.payload.queryParams) {
              action.payload.queryParams.forEach((queryParam) => {
                redirect.queryParams.push({
                  key: queryParam.key,
                  value: queryParam.value,
                });
              });
            }
          }
        }
      }
    },
    updateDestinationUrl: (
      draft,
      action: PayloadAction<{
        redirectId: string;
        value: string;
        queryParams?: { key: string; value: string }[];
      }>
    ) => {
      const index = draft.redirects.findIndex(
        (redirect) => redirect.id === action.payload.redirectId
      );
      if (index !== -1) {
        draft.redirects[index].destinationUrl = action.payload.value;
        if (action.payload.queryParams) {
          action.payload.queryParams.forEach((queryParam) => {
            draft.redirects[index].queryParams.push({
              key: queryParam.key,
              value: queryParam.value,
            });
          });
        }
      }
    },
    deleteRedirectOrigin: (
      draft,
      action: PayloadAction<{
        group: number;
      }>
    ) => {
      draft.redirects = ensureRedirectGroupOrder(
        draft.redirects.filter((item) => item.group !== action.payload.group)
      );
    },
    updateRedirectGroupFilter: (
      draft,
      action: PayloadAction<{ group: number; filter: RedirectFilter }>
    ) => {
      for (const redirect of draft.redirects) {
        if (redirect.group === action.payload.group) {
          redirect.filter = action.payload.filter;
        }
      }
    },
    updateRedirectType: (
      draft,
      action: PayloadAction<{ group: number; redirectType: UrlRedirectType }>
    ) => {
      for (const redirect of draft.redirects) {
        if (redirect.group === action.payload.group) {
          redirect.redirectType = action.payload.redirectType;
          if (action.payload.redirectType === "fullReplacement") {
            redirect.find = undefined;
          }
        }
      }
    },
    updateRedirectFind: (
      draft,
      action: PayloadAction<{ redirectId: string; find: string }>
    ) => {
      const index = draft.redirects.findIndex(
        (redirect) => redirect.id === action.payload.redirectId
      );
      if (index !== -1) {
        draft.redirects[index].find = action.payload.find;
      }
    },
    updateQueryParam: (
      draft,
      action: PayloadAction<{
        redirectId: string;
        queryParamIndex: number;
        key: string;
        value: string;
      }>
    ) => {
      const index = draft.redirects.findIndex(
        (redirect) => redirect.id === action.payload.redirectId
      );
      if (index !== -1) {
        draft.redirects[index].queryParams[action.payload.queryParamIndex] = {
          key: action.payload.key,
          value: action.payload.value,
        };
      }
    },
    deleteQueryParam: (
      draft,
      action: PayloadAction<{
        redirectId: string;
        queryParamIndex: number;
        isControl?: boolean;
      }>
    ) => {
      const index = draft.redirects.findIndex(
        (redirect) => redirect.id === action.payload.redirectId
      );
      if (index !== -1) {
        draft.redirects[index].queryParams.splice(
          action.payload.queryParamIndex,
          1
        );
        // for template redirects
        if (action.payload.isControl) {
          draft.redirects[index].templateType = null;
        }
      }
    },
    addQueryParam: (
      draft,
      action: PayloadAction<{
        redirectId: string;
      }>
    ) => {
      const index = draft.redirects.findIndex(
        (redirect) => redirect.id === action.payload.redirectId
      );
      if (index !== -1) {
        draft.redirects[index].queryParams.push({
          key: "",
          value: "",
        });
      }
    },
    updateRedirectOnce: (
      draft,
      action: PayloadAction<{
        group: number;
        value: boolean;
      }>
    ) => {
      draft.redirects.forEach((redirect) => {
        if (redirect.group === action.payload.group) {
          redirect.redirectOnce = action.payload.value;
        }
      });
    },
    initializeThemeRedirect: (
      draft,
      action: PayloadAction<{
        variations: VariationFe[];
        isPersonalization?: boolean;
      }>
    ) => {
      const group = (maxBy(draft.redirects, (_) => _.group)?.group || 0) + 1;
      action.payload.variations.forEach((variation: VariationFe) => {
        draft.redirects.push(
          initialRedirectState({
            variation,
            group,
            isThemeTest: true,
            skip: action.payload.isPersonalization ? false : undefined,
          })
        );
      });
    },
    updateTemplateRedirect: (
      draft,
      action: PayloadAction<{
        redirectState: RedirectDto;
        isControl: boolean;
        isUserTriggered?: boolean;
      }>
    ) => {
      let draftCopy: RedirectDto[] = cloneDeep(draft.redirects);

      const index = draftCopy.findIndex((red) => {
        return (
          red.variationId === action.payload.redirectState.variationId &&
          red.group === action.payload.redirectState.group &&
          red.isTemplateTest
        );
      });

      if (index !== -1) {
        draftCopy[index] = action.payload.redirectState;

        if (action.payload.isControl) {
          draftCopy = ensureAllTemplateType(
            draftCopy,
            action.payload.redirectState.templateType!,
            action.payload.redirectState.group
          );
        }

        draftCopy = ensureTemplateSuffix(
          draftCopy,
          action.payload.redirectState.group
        );
      }
      draft.redirects = draftCopy;
      draftCopy.sort((a, b) => (a.group ?? 0) - (b.group ?? 0));
      if (action.payload.isUserTriggered === false) {
        draft.storedRedirects = draft.redirects;
      }
    },
    removeTemplateRedirectGroup: (
      draft,
      action: PayloadAction<{ group: number; variationId?: string }>
    ) => {
      const { group, variationId } = action.payload;

      draft.redirects = draft.redirects.filter((redirect) => {
        if (variationId) {
          return (
            redirect.group !== group || redirect.variationId !== variationId
          );
        }
        return redirect.group !== group;
      });
      draft.redirects.sort((a, b) => (a.group ?? 0) - (b.group ?? 0));
    },
    updatePersonalizationTemplateRedirect: (
      draft,
      action: PayloadAction<{
        redirectState: RedirectDto;
        index: number;
        tabValue?: number;
      }>
    ) => {
      const draftCopy: RedirectDto[] = cloneDeep(draft.redirects);
      const redirectIndex = draftCopy.findIndex(
        (redirect) =>
          redirect.group === action.payload.redirectState.group &&
          redirect.variationId === action.payload.redirectState.variationId &&
          redirect.isTemplateTest
      );

      if (redirectIndex !== -1) {
        const currentRedirect = draftCopy[redirectIndex];

        if (action.payload.index === 0) {
          currentRedirect.templateSuffixes =
            action.payload.redirectState.templateSuffixes ?? [];

          if (
            currentRedirect.templateType &&
            currentRedirect.queryParams.length &&
            !currentRedirect.queryParams[0]?.value.includes(
              currentRedirect.templateType
            )
          ) {
            currentRedirect.queryParams = [];
          }
        } else if (action.payload.index === 1) {
          currentRedirect.queryParams =
            action.payload.redirectState.queryParams ?? [];
        }

        currentRedirect.templateType =
          action.payload.redirectState.templateType;
      } else {
        draftCopy.push(action.payload.redirectState);
      }

      draft.redirects = draftCopy;
      draftCopy.sort((a, b) => (a.group ?? 0) - (b.group ?? 0));
    },
    updateThemeRedirect: (
      draft,
      action: PayloadAction<{
        redirectState: RedirectDto;
      }>
    ) => {
      const draftCopy: RedirectDto[] = cloneDeep(draft.redirects);

      const index = draftCopy.findIndex(
        (red) =>
          red.variationId === action.payload.redirectState.variationId &&
          red.isThemeTest
      );

      if (index !== -1) {
        draftCopy[index] = action.payload.redirectState;
      }
      draft.redirects = draftCopy;
    },
    deleteAllTemplateOrigins: (draft) => {
      draft.redirects = draft.redirects.filter((item) => !item.isTemplateTest);
    },
    deleteThemeRedirects: (draft) => {
      draft.redirects = draft.redirects.filter((item) => !item.isThemeTest);
    },
    initializeTemplateRedirects: (
      draft,
      action: PayloadAction<{
        variations: VariationFe[];
        templateType: ShopifyTemplateTypeType | null;
        isPersonalization?: boolean;
        group?: number;
        templateSuffixes?: string[];
        isUserTriggered?: boolean;
      }>
    ) => {
      action.payload.variations.forEach((variation: VariationFe) => {
        draft.redirects.push(
          initialRedirectState({
            variation,
            group: action.payload.group ?? 1,
            isThemeTest: false,
            templateType: action.payload.templateType ?? null,
            templateSuffixes: action.payload.templateSuffixes ?? [],
            isTemplateTest: true,
            skip: action.payload.isPersonalization ? false : undefined,
          })
        );
      });
      draft.redirects.sort((a, b) => (a.group ?? 0) - (b.group ?? 0));
      if (action.payload.isUserTriggered === false) {
        draft.storedRedirects = draft.redirects;
      }
    },
    setRedirectErrors: (
      draft,
      action: PayloadAction<{
        error: string | null;
        redirectId: string;
        urlKey: string;
      }>
    ) => {
      if (action.payload.error) {
        if (!draft.redirectErrors.includes(action.payload.error)) {
          draft.redirectErrors.push(action.payload.error);
        }
      } else {
        const index = draft.redirectErrors.findIndex(
          (err) =>
            err === `${action.payload.urlKey}-${action.payload.redirectId}`
        );
        if (index !== -1) {
          draft.redirectErrors.splice(index, 1);
        }
      }
    },
    removeDeletedRedirectErrors: (draft) => {
      const currentRedirectErrors: string[] = [];
      const allRedirectIds = draft.redirects.map((redirect) => redirect.id);

      draft.redirectErrors.forEach((error) => {
        const formattedError = error.replace(
          /^(originUrl-|destinationUrl-|queryParameter-)/,
          ""
        );

        if (allRedirectIds.includes(formattedError)) {
          currentRedirectErrors.push(error);
        }
      });
      draft.redirectErrors = currentRedirectErrors;
    },
    resetRedirectErrors: (draft) => {
      draft.redirectErrors = [];
    },
  },
  selectors: {
    selectedRedirectsState: (state) => state as IRedirectsSliceState,
    selectedRedirects: (state) => state.redirects,
    selectedStoredRedirects: (state) => state.storedRedirects,
    selectedRedirectErrors: (state) => state.redirectErrors,
  },
});

export const {
  resetRedirects,
  initRedirectsState,
  initUrlRedirectsState,
  checkRedirects,
  addNewRedirectOrigin,
  updateAllRedirectFilters,
  deleteAllUrlRedirects,
  updateOriginUrl,
  updateDestinationUrl,
  deleteRedirectOrigin,
  updateRedirectGroupFilter,
  updateRedirectType,
  updateRedirectFind,
  updateQueryParam,
  deleteQueryParam,
  addQueryParam,
  updateRedirectOnce,
  initializeThemeRedirect,
  deleteAllTemplateOrigins,
  deleteThemeRedirects,
  updateTemplateRedirect,
  updateThemeRedirect,
  initializeTemplateRedirects,
  removeTemplateRedirectGroup,
  setRedirectErrors,
  removeDeletedRedirectErrors,
  resetRedirectErrors,
  updateAllUrlRedirectType,
  setOfferTestRedirects,
  resetOfferTestRedirects,
  initRedirectsFromVariationsState,
  // personalization
  updatePersonalizationTemplateRedirect,
} = redirectsSlice.actions;

export const redirectsReducer = redirectsSlice.reducer;
export const {
  selectedRedirectsState,
  selectedRedirects,
  selectedStoredRedirects,
  selectedRedirectErrors,
} = redirectsSlice.selectors;

export const getSliceSpecificRedirects = (
  redirectType: "template" | "theme" | "url"
) =>
  createSelector(selectedRedirects, (redirects) =>
    getSpecificRedirectsUtil(redirects, redirectType)
  );

export const getSliceVariationSpecificRedirects = (
  redirectType: "template" | "theme" | "url",
  variationId: string
) =>
  createSelector(selectedRedirects, (redirects) =>
    getSpecificVariationRedirectsUtil(redirects, redirectType, variationId)
  );
